import './InstallationStepCard.scss';
import { useAuth } from 'context/useAuth';
import Button from 'components/Button/Button';
import { Timestamp } from 'firebase/firestore';
import { useAppSettings } from 'context/appsettingsContext';
import React, { useEffect, useState, Suspense } from 'react';
import InstallationStepCardProps from './InstallationStepCardProps';
import { ReactComponent as PassIcon } from 'assets/icons/check-mark.svg';
import { adminUnlock, delaySeconds, getDisplayDateFromTimestamp, reApplyConfiguration } from 'functions/deviceStatusUtils';

const InstallationStepCard: React.FC<InstallationStepCardProps> = (stepProps) => {

  const settings = useAppSettings();
  const { authorisedUser } = useAuth();
  const [Component, setComponent] = useState<React.ComponentType<any> | null>(null);

  async function refresh() {
    
    stepProps.step.initialized = false;
    
    stepProps.step.preconditions = {};
    stepProps.step.test = {};

    stepProps.step.dateStarted = Timestamp.now();
    stepProps.step.dateFailed = null;
    stepProps.step.dateVerified = null;
    stepProps.step.preconditionsDateVerified = null;
    
    stepProps.step.failDescription = null;

    if (stepProps.installation.deviceId) {

        try {
            await adminUnlock(settings.handleLockCommandsUrl, authorisedUser.accessToken, stepProps.installation.deviceId);
        } catch(error: any) {
           console.error('Failed to admin unlock', error);
        }

        await delaySeconds(1);

        try {
            await reApplyConfiguration(settings.reapplyConfigurationUrl, authorisedUser.accessToken, stepProps.installation.deviceId);
        } catch(error: any) {
           console.error('Failed to reapply configuration', error);
        }
    }

    await stepProps.update();
  }

  async function fail() {
    
    stepProps.step.dateFailed = Timestamp.now();
    
    let failDescription = null;
    
    while (!(failDescription = window.prompt('Reason:'))) {
      stepProps.step.failDescription = failDescription;
    }

    await stepProps.update();
  }

  async function skip() {
    stepProps.step.test.skipped = true;
    stepProps.step.dateVerified = Timestamp.now();
    await stepProps.update();
    return;
  }

  useEffect(() => {

    let isMounted = true;

    const loadComponent = async () => {
      try {
        
        const module = await import(`./Steps/${stepProps.step.code}.tsx`);
        
        if (isMounted) {
          setComponent(() => module.default);
        }

      } catch (error) {
        
        console.error(`Failed to load component: ${stepProps.step.code}`, error);
        
        if (isMounted) {
          setComponent(null);
        }
      }
    };

    loadComponent();

    return () => {
      isMounted = false;
    };
  }, [stepProps.step.code]);

  const componentVisible = stepProps.index === 0 || (stepProps.installation.steps[stepProps.index - 1] || {}).dateVerified;

  if (!componentVisible) {
    return <></>;
  }

  return (
    <div className="installation-step-card">
      <div className="installation-step-card__header" style={{ paddingBottom: stepProps.step.dateVerified || stepProps.step.dateFailed ? '16px' : '0' }}>
        <div className="installation-step-card__header-title" style={{ backgroundColor: "white", fontSize: '18px' }}>
          <b>{stepProps.index + 1} : {stepProps.step.name}</b>
          <img style={{ display: !stepProps.step.dateVerified && !stepProps.step.dateFailed && !stepProps.step.initialized ? 'block' : 'none' }} src='/spinner.gif' width={30} height={30} alt='Step Initializing' />
        </div>
      </div>
      <div style={{ display: !stepProps.step.dateVerified && !stepProps.step.dateFailed ? 'flex' : 'none', justifyContent: 'space-between', alignItems: 'center', fontSize: 'smaller', whiteSpace: 'nowrap', width: '100%', paddingLeft: '16px', paddingRight: '16px', paddingBottom: '16px' }}>
        {/* <Button style={{ backgroundColor: '#C70039', width: '80px' }} xsmall label='Fail' onClick={() => fail()} /> */}
        <Button style={{ width: '80px' }} xsmall label='Restart' onClick={() => refresh()} />
        <Button style={{ width: '80px', display: stepProps.step.allowSkip ? 'block' : 'none' }} xsmall label='Skip' onClick={() => skip()} />
      </div>
      <div className="installation-step-card__body" >
        <div style={{ display : stepProps.step.test.skipped ? 'none' : undefined }}>
          <Suspense fallback={<div>Loading...</div>}>
            {Component ? <Component {...stepProps} /> : <div>Component not found</div>}
          </Suspense>
        </div>
        <div style={{ display: stepProps.step.dateVerified ? 'block' : 'none', marginTop: '7px', paddingTop: '7px' }} >
          <b>
            Outcome
          </b>
          <table className='pass' style={{ padding: 0, margin: 0, border: 0 }} >
            <tbody>
              <tr>
                <td>
                  - { stepProps.step.test?.skipped ? 'Skipped' : 'Passed' } @ {getDisplayDateFromTimestamp(stepProps.step.dateVerified)} <PassIcon className={ stepProps.step.test?.skipped ? 'skip' : 'pass' } style={{ float: 'right' }} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ display: stepProps.step.dateFailed ? 'block' : 'none', marginTop: '7px', paddingTop: '7px' }} >
          <b>
            Outcome : 
          </b>
          <table className='fail' style={{ padding: 0, margin: 0, border: 0 }} >
            <tbody>
              <tr>
                <td>
                  - Failed @ {getDisplayDateFromTimestamp(stepProps.step.dateFailed)}
                </td>
              </tr>
              <tr>
                <td>
                  - {stepProps.step.failDescription}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InstallationStepCard;